import React from 'react';
import { GRADE_STYLES } from './gradeOverlay';
import { noteName, pitchName } from '../../utils/noteSpelling';

/**
 * The note inspector: hovering over a graded note shows what was heard for
 * it. A small chart plots the sung pitch around the note against its
 * target (cents from the note's equal-tempered pitch), marking which part
 * of the singing was lined up with this note and which part was graded,
 * and a few lines say when the note came in, how long it took to settle
 * and how much singing the grade rests on. Its buttons play the note back
 * from the take, or the target pitch, once the take is over.
 */

const WIDTH = 300;
const CHART_HEIGHT = 120;
const PAD = { left: 30, right: 8, top: 8, bottom: 8 };
const MINUS = '−';
const HOP_SECONDS = 0.01;

const signed = (cents) => (cents > 0 ? `+${cents}` : cents < 0 ? `${MINUS}${-cents}` : '0');
const seconds = (s) => `${Math.abs(s).toFixed(2)} s`;
// Cents from the note's pitch, octave errors folded in
const centsFrom = (midi, base) => {
  const c = (midi - base) * 100;
  return c - 1200 * Math.round(c / 1200);
};

/** "G♯4 · in tune (+5¢)", "G♯4 · 30¢ flat, scooped", "G♯4 · sang A (+95¢)" */
export function headline(result, name) {
  const { grade, cents } = result;
  if (grade === 'missed') return `${name} · not heard`;
  const scooped = result.scoop ? (result.scoop.direction === 'up' ? ', scooped' : ', slid in') : '';
  if (scooped) return `${headline({ ...result, scoop: null }, name)}${scooped}`;
  if (grade === 'good') return `${name} · in tune (${signed(cents)}¢)`;
  if (grade === 'close') return `${name} · ${Math.abs(cents)}¢ ${cents > 0 ? 'sharp' : 'flat'}`;
  return `${name} · sang ${pitchName(result.sung, result.fifths)} (${signed(cents)}¢)`;
}

function facts(result, tuning) {
  const { detail, entry, settle, grade } = result;
  const lines = [];
  if (grade === 'missed') {
    lines.push('Nothing sung for this note.');
  } else if (entry === null) {
    lines.push('Sung, but not as written: graded on what was heard in its written time.');
  } else if (Math.abs(entry) < 0.03) {
    lines.push('Came in on time.');
  } else {
    // Timing is measured against how this take ran overall (the headset's
    // delay is taken out with the singer's usual timing)
    lines.push(`Came in ${seconds(entry)} ${entry > 0 ? 'later' : 'earlier'} than your other notes.`);
  }
  if (grade !== 'missed') {
    if (result.scoop) {
      const { direction, cents, seconds: took } = result.scoop;
      lines.push(direction === 'up'
        ? `Scooped up from ${cents}¢ below; on pitch after ${seconds(took)}. The grade starts from there.`
        : `Slid down from ${cents}¢ above; on pitch after ${seconds(took)}. The grade starts from there.`);
    } else if (settle >= 0.03) {
      lines.push(`Settled after ${seconds(settle)} (not counted).`);
    }
    const graded = detail.trace.filter(p => p[2] === 2).length * HOP_SECONDS;
    lines.push(`Graded on ${seconds(graded)} of singing.`);
  }
  if (tuning === 'just') {
    const middle = (detail.start + detail.end) / 2;
    const stretch = detail.targets.find(t => middle < t.to) || detail.targets[detail.targets.length - 1];
    const offset = Math.round((stretch.midi - detail.midi) * 100);
    lines.push(offset === 0 ? 'Just target: the piano’s pitch.' : `Just target: ${signed(offset)}¢ from the piano’s pitch.`);
  }
  return lines;
}

function Chart({ result }) {
  const { detail, grade } = result;
  const color = GRADE_STYLES[grade].color;
  const from = detail.start - 0.3;
  const to = detail.end + 0.3;
  const targets = detail.targets.map(t => ({ ...t, cents: centsFrom(t.midi, detail.midi) }));
  const sung = detail.trace.filter(p => p[1] !== null).map(([time, midi, role]) => ({ time, cents: centsFrom(midi, detail.midi), role }));

  // Vertical range: at least +-100 cents around the targets, widened to
  // take in this note's singing, at most +-300
  const anchor = targets.reduce((sum, t) => sum + t.cents, 0) / targets.length;
  let span = 100;
  sung.filter(p => p.role > 0).forEach((p) => { span = Math.max(span, Math.abs(p.cents - anchor) + 20); });
  span = Math.min(300, Math.ceil(span / 50) * 50);
  const top = anchor + span;
  const bottom = anchor - span;

  const innerWidth = WIDTH - PAD.left - PAD.right;
  const innerHeight = CHART_HEIGHT - PAD.top - PAD.bottom;
  const x = (time) => PAD.left + ((time - from) / (to - from)) * innerWidth;
  const y = (cents) => PAD.top + ((top - Math.max(bottom, Math.min(top, cents))) / (top - bottom)) * innerHeight;
  const ticks = [anchor - span, anchor - span / 2, anchor, anchor + span / 2, anchor + span];

  return (
    <svg className="dps-inspector-chart" width={WIDTH} height={CHART_HEIGHT} viewBox={`0 0 ${WIDTH} ${CHART_HEIGHT}`} aria-hidden="true">
      <rect x={PAD.left} y={PAD.top} width={innerWidth} height={innerHeight} fill="#f8fafc" />
      {/* the note's written time */}
      <rect x={x(detail.start)} y={PAD.top} width={x(detail.end) - x(detail.start)} height={innerHeight} fill="#e2e8f0" />
      {ticks.map(t => (
        <g key={t}>
          <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y(t)} y2={y(t)} stroke="#e2e8f0" strokeWidth="0.5" />
          <text x={PAD.left - 4} y={y(t)} textAnchor="end" dominantBaseline="central" fontSize="9" fill="#94a3b8">
            {signed(Math.round(t - anchor))}
          </text>
        </g>
      ))}
      {/* in tune: within 25 cents of the target */}
      {targets.map(t => (
        <rect key={`band-${t.from}`} x={x(Math.max(from, t.from))} y={y(t.cents + 25)} width={Math.max(0, x(Math.min(to, t.to)) - x(Math.max(from, t.from)))} height={y(t.cents - 25) - y(t.cents + 25)} fill="#16a34a" fillOpacity="0.12" />
      ))}
      {targets.map(t => (
        <line key={`target-${t.from}`} x1={x(Math.max(from, t.from))} x2={x(Math.min(to, t.to))} y1={y(t.cents)} y2={y(t.cents)} stroke="#334155" strokeWidth="1.2" strokeDasharray="4 3" />
      ))}
      {/* Neighbours' singing beyond the range is left out; this note's is
          pinned to the edge so its direction still shows */}
      {sung.filter(p => p.role > 0 || (p.cents <= top && p.cents >= bottom)).map(p => (
        <circle
          key={p.time}
          cx={x(p.time)}
          cy={y(p.cents)}
          r={p.role === 2 ? 1.9 : 1.5}
          fill={p.role === 0 ? '#94a3b8' : color}
          fillOpacity={p.role === 0 ? 0.6 : p.role === 1 ? 0.35 : 1}
        />
      ))}
    </svg>
  );
}

const GradeInspector = ({ inspect, tuning, onHold, canHear, hearing, onHearTake, onHearTarget, onStop }) => {
  if (!inspect || !inspect.result.detail) return null;
  const { result, rect } = inspect;
  const name = noteName(result.detail.midi, result.fifths);
  // Above the note when there is room, otherwise below; kept on screen
  const height = CHART_HEIGHT + 150;
  const below = rect.top < height + 16;
  const centre = (rect.left + rect.right) / 2;
  const left = Math.max(8, Math.min(window.innerWidth - WIDTH - 24, centre - (WIDTH + 16) / 2));
  const style = below ? { left, top: rect.bottom + 10 } : { left, bottom: window.innerHeight - rect.top + 10 };
  const hearTitle = canHear ? undefined : 'Stop playback to listen';
  return (
    <div
      className="dps-inspector"
      style={style}
      role="dialog"
      aria-label={`Inspect ${name}`}
      onMouseEnter={() => onHold(true)}
      onMouseLeave={() => onHold(false)}
    >
      <div className="dps-inspector-head" style={{ color: GRADE_STYLES[result.grade].color }}>
        {headline(result, name)}
        <span className="dps-inspector-pass">pass {result.pass + 1}</span>
      </div>
      <Chart result={result} />
      <div className="dps-inspector-key">
        <span><i className="graded" style={{ background: GRADE_STYLES[result.grade].color }} />graded</span>
        <span><i style={{ background: GRADE_STYLES[result.grade].color, opacity: 0.35 }} />not counted</span>
        <span><i style={{ background: '#94a3b8' }} />around it</span>
        <span><i className="band" />{'±'}25{'¢'}</span>
      </div>
      <ul className="dps-inspector-facts">
        {facts(result, tuning).map(line => <li key={line}>{line}</li>)}
      </ul>
      <div className="dps-inspector-listen">
        <button
          type="button"
          disabled={!canHear || !result.detail.listen}
          title={hearTitle || 'Play this note from your take'}
          onClick={() => (hearing === 'take' ? onStop() : onHearTake(result))}
        >
          {hearing === 'take' ? '■ Stop' : '▶ My singing'}
        </button>
        <button
          type="button"
          disabled={!canHear}
          title={hearTitle || (tuning === 'just' ? 'Play the just-tuned target' : 'Play the target pitch')}
          onClick={() => (hearing === 'target' ? onStop() : onHearTarget(result))}
        >
          {hearing === 'target' ? '■ Stop' : '▶ Target'}
        </button>
        {!canHear && <span>{hearTitle}</span>}
      </div>
    </div>
  );
};

export default GradeInspector;
